import React, { useEffect, useRef, useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import AuthLayout, {
  AuthField, EnterButton, IconAlert, IconCheck,
} from '../components/AuthLayout';

const ENDPOINT = 'https://dev.sliitleo.org/Backend/update_password_to_nic.php';
const ID_PATTERN = /^[A-Z]{2}\d{6,}$/;

export default function ForgotPassword() {
  usePageTitle('Reset password');

  const [id, setId] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [msg, setMsg] = useState('');
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);
  const lionRef = useRef(null);

  // Desktop only -- on a phone this would raise the keyboard over the screen.
  useEffect(() => {
    if (window.matchMedia('(min-width: 900px)').matches) inputRef.current?.focus();
  }, []);

  const fail = (message) => {
    setStatus('error');
    setMsg(message);
    setShake(true);
    lionRef.current?.shudder();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;

    const sliit_id = id.trim().toUpperCase();
    if (!sliit_id) return fail('Enter your SLIIT ID.');
    if (!ID_PATTERN.test(sliit_id)) return fail('That ID looks incorrect. Check it and try again.');

    setStatus('loading');
    setMsg('');
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sliit_id }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        return fail(`Server error (${res.status}). Please try again.`);
      }

      if (!res.ok || !data?.success) return fail(data?.message || `Request failed (${res.status}).`);
      setStatus('success');
      lionRef.current?.pulse(9);
    } catch {
      fail('Could not reach the server. Please try again.');
    }
  };

  const loading = status === 'loading';
  const hasError = status === 'error';
  const idLooksValid = ID_PATTERN.test(id.trim().toUpperCase());
  let i = 0;

  if (status === 'success') {
    return (
      <AuthLayout lionRef={lionRef}>
        <div className="done-mark"><IconCheck /></div>
        <h1 className="auth-title rise" style={{ '--i': i++ }}>Password reset</h1>
        <p className="auth-sub rise" style={{ '--i': i++ }}>
          Sign in with your NIC number as the password.
        </p>
        <EnterButton className="rise" style={{ '--i': i++ }} label="Sign in" href="/login" />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout lionRef={lionRef}>
      <h1 className="auth-title rise" style={{ '--i': i++ }}>Reset password</h1>

      <form onSubmit={handleSubmit} noValidate>
        <div
          className={`fields rise ${shake ? 'shake' : ''}`}
          style={{ '--i': i++ }}
          onAnimationEnd={(e) => { if (e.animationName === 'auth-shake') setShake(false); }}
        >
          <AuthField
            id="rs-id"
            label="SLIIT ID"
            valid={idLooksValid && !hasError}
            invalid={hasError}
            trailing={idLooksValid && !hasError ? (
              <i className="fld-ok" aria-hidden="true"><IconCheck size={18} /></i>
            ) : null}
          >
            <input
              id="rs-id"
              ref={inputRef}
              className="auth-input is-id"
              type="text"
              value={id}
              onChange={(e) => { setId(e.target.value.toUpperCase()); lionRef.current?.pulse(1); }}
              placeholder="IT00000000"
              autoComplete="username"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={loading}
              aria-invalid={hasError}
            />
          </AuthField>
        </div>

        <div aria-live="polite">
          {hasError && (
            <div className="err" role="alert">
              <IconAlert />
              <span>{msg}</span>
            </div>
          )}
        </div>

        <p className="note rise" style={{ '--i': i++ }}>Your password will be set to your NIC.</p>

        <EnterButton
          className="rise"
          style={{ '--i': i++ }}
          label="Reset"
          busyLabel="Resetting"
          busy={loading}
        />
      </form>

      <p className="alt rise" style={{ '--i': i++ }}>
        Remembered it?
        <a href="/login">Sign in</a>
      </p>
    </AuthLayout>
  );
}
